/**
 * SociShop's refill ("telafi") policy — the ONE place the storefront decides a refill term.
 *
 * OWNER POLICY (DECISIONS.md D-06 / D-07 / D-08, ADR-006 Q26; REQUIREMENTS R02)
 * ----------------------------------------------------------------------------
 * Every package has a refill. Its length depends only on the package's USD LIST price
 * (`packages.price_en`), the same band in all six locales — a customer paying in lira gets the
 * band of the package's dollar list price, never a band computed from the lira amount or an
 * exchange rate:
 *
 *     [0, 100) USD → 30 days    [100, 300) USD → 90 days    ≥ 300 USD → 365 days
 *
 * Both lower edges are inclusive: exactly 100.00 is 90 days, exactly 300.00 is 365 days.
 * A coupon, a VIP discount or any other reduction never lowers the band (the list price is the
 * basis, not what was charged). A custom quantity takes the USD list amount of the same
 * package curve for that quantity, computed exactly as the API stamps it (`refillListBasisForQuote`).
 *
 * SociShop funds the refill whatever the supplier says (D-08). The supplier's own wording
 * ("Telafisiz", "30 Gün Telafili", "no refill") is recorded in the claim matrix as a supplier
 * fact and NEVER decides what a customer is promised. This module replaced the old per-group,
 * supplier-derived `refillTermsForGroup()` (ADR-006 Q21 / ADR-007 R8, retired by D-08).
 *
 * WHAT THE CUSTOMER GETS (D-07): drops that happen after delivery are refilled free, up to the
 * delivered quantity, within the window. The window starts when delivery finishes
 * (`orders.completed_at`). Not covered: an account made private, deleted or restricted, and
 * drops caused by the customer's own actions. Under-delivery is a re-delivery or refund matter,
 * not a refill.
 *
 * WHO STAMPS IT: the API stamps the term onto the order when the order row is written
 * (`orders.refill_days`, `refill_policy_version`, `refill_basis_usd_minor`; backend
 * `models.RefillDaysForUSDMinor`, same bands, same version string). An order's own surfaces
 * (order success, the customer panel, the admin) show THAT stamp. An order written before the
 * policy existed carries NULL and keeps the terms it was sold under (ADR-006 Q23): it is never
 * shown a band computed today (`orderRefillTerm` answers `legacy`).
 *
 * Kept dependency-free and tiny: it ships to the package cards, the checkout and the panel.
 */

/** Names this exact band table. Must equal backend `models.RefillPolicyVersion`. */
export const REFILL_POLICY_VERSION = "refill-2026-10-01";

export type RefillDays = 30 | 90 | 365;

export interface RefillBand {
    /** Lowest USD list price of the band, in cents, inclusive. */
    readonly fromUsdMinor: number;
    readonly days: RefillDays;
}

/** The bands, lowest first. The next band's `fromUsdMinor` is this band's exclusive upper edge. */
export const REFILL_BANDS: readonly RefillBand[] = [
    { fromUsdMinor: 0, days: 30 },
    { fromUsdMinor: 10_000, days: 90 },
    { fromUsdMinor: 30_000, days: 365 },
];

/** Every refill length the policy can produce, ascending. */
export const REFILL_POLICY_DAYS: readonly RefillDays[] = REFILL_BANDS.map((band) => band.days);

/** The two price edges in whole dollars (100, 300), for copy that states the bands. */
export const REFILL_EDGES_USD: readonly number[] = REFILL_BANDS.slice(1).map((band) => band.fromUsdMinor / 100);

const DAY_MS = 24 * 60 * 60 * 1000;

export function isRefillDays(value: unknown): value is RefillDays {
    return typeof value === "number" && (REFILL_POLICY_DAYS as readonly number[]).includes(value);
}

/**
 * The refill length for a USD list price in cents. `null` for a negative, fractional or non-finite
 * amount: such a price states no term, and the caller must not invent one.
 */
export function refillDaysForUsdMinor(minor: number): RefillDays | null {
    if (typeof minor !== "number" || !Number.isInteger(minor) || minor < 0) return null;
    let days: RefillDays | null = null;
    for (const band of REFILL_BANDS) if (minor >= band.fromUsdMinor) days = band.days;
    return days;
}

/**
 * A USD amount in dollars → cents, the way the database casts `(price_en*100)::bigint`
 * (half away from zero; catalogue prices carry two decimals). `null` for a negative or
 * non-finite amount.
 */
export function usdToMinor(usd: number | null | undefined): number | null {
    if (typeof usd !== "number" || !Number.isFinite(usd) || usd < 0) return null;
    return Math.round(usd * 100);
}

/** The refill length for a USD list price in dollars, or null when the price states none. */
export function refillDaysForUsd(usd: number | null | undefined): RefillDays | null {
    const minor = usdToMinor(usd);
    return minor === null ? null : refillDaysForUsdMinor(minor);
}

/**
 * A catalogue package as the storefront receives it: the API row (`price_en`) or a mapped
 * card row (`priceEn`). Only the USD LIST price is read — never `price_tr`, a charged amount,
 * a discounted amount or `orig_price_en`.
 */
export interface UsdListPriced {
    price_en?: number | null;
    priceEn?: number | null;
}

/** The package's refill length from its USD list price, or null when the row carries none. */
export function refillDaysForPackage(pkg: UsdListPriced | null | undefined): RefillDays | null {
    if (!pkg) return null;
    const usd = typeof pkg.price_en === "number" ? pkg.price_en : pkg.priceEn;
    return refillDaysForUsd(usd);
}

// ── A custom quantity / quote line: the API's refill basis, ported exactly ─────────────────────

/*
 * WHY A PORT (G03-V2 risk 1, INTEGRATE-1 Task B, 2026-10-02)
 * The package selector's custom-quantity widget shows a refill band BEFORE purchase. The order is
 * stamped by the API from QuoteService.quoteRefillListBasis (backend_combined/internal/service/
 * quote_service.go) and models.RefillListBasis (internal/models/refill_list_basis.go):
 *
 *   1. the quote locale's ladder decides the curve: its SELLABLE packages (is_active and a positive
 *      price in the locale's pricing currency: price_tr for tr, price_en otherwise), sorted by
 *      quantity; no package, a non-positive quantity or two packages with one quantity → no curve
 *      (service.NewPriceCurve);
 *   2. the curve point never extrapolates: a quantity outside [q_1, q_n] has no basis; a package
 *      quantity is one knot, anything else the two knots around it (PriceCurve.Price);
 *   3. the USD list prices (price_en, cents) of THOSE packages give
 *        P(q) = p_lo + floor((p_hi − p_lo)·(q − q_lo) / (q_hi − q_lo))      (total-linear-floor/v1)
 *      in exact integer arithmetic, a knot without a USD price → no basis;
 *   4. the line amount is P(curve quantity) × the target count for per-post / per-video / plan
 *      lines, × 1 for a custom quantity or split total (models.QuoteComponentCurveInput);
 *   5. the band is refillDaysForUsdMinor(Σ lines).
 *
 * The widget used to interpolate the UNIT price between packages, extrapolate past the ladder ends
 * and ignore the multiplier, so near 100/300 USD it could promise a band the order would not get
 * (e.g. 1,130 units between $90.00 @ 1,000 and $200.00 @ 2,500: unit interpolation $100.72 → 90
 * days, the API $99.53 → 30 days). The shared vectors in backend_combined/internal/service/testdata/
 * refill_list_basis_vectors.json pin this port and the API to the same answers (Go:
 * refill_list_basis_vectors_test.go; TS: lib/__tests__/refill-list-basis-vectors.test.ts).
 */

/** One package of the quote locale's ladder, as the refill basis reads it. */
export interface RefillLadderRow {
    /** The package id, when known (two knots must be different packages). */
    packageId?: string;
    quantity: number;
    /**
     * Sellable in the quote's locale: active, and a positive price in the locale's pricing currency
     * (price_tr for tr, price_en otherwise). Only sellable packages are curve knots.
     */
    sellable: boolean;
    /** The package's USD list price (price_en) in cents; null/undefined when it has none. */
    usdMinor: number | null | undefined;
}

/** One quote line: the curve quantity and what the point is multiplied by. */
export interface RefillQuoteLine {
    curveQuantity: number;
    multiplier: number;
}

/** The quote component kinds the API can state a refill basis for (models.QuoteComponentCurveInput). */
export type RefillQuoteKind = "custom_quantity" | "split_total" | "per_post_each" | "per_video_each" | "future_posts_plan";

export interface RefillListBasis {
    /** The USD list amount in cents (Σ lines). */
    amountUsdMinor: number;
    /** Per line, the one or two ladder packages its point was read from (lower knot first). */
    lines: { knots: RefillLadderRow[]; curveQuantity: number; multiplier: number }[];
    days: RefillDays;
}

const MAX_INT32 = 2_147_483_647;

function isPositiveInt(n: unknown): n is number {
    return typeof n === "number" && Number.isInteger(n) && n > 0;
}

/**
 * models.QuoteComponentCurveInput: a custom quantity and a split total are one curve point at their
 * total; per-post, per-video and plan lines are the curve at the per-target quantity × the target
 * count. `null` for any other kind or a line whose units do not make its total.
 */
export function refillQuoteLineFor(kind: string, totalQuantity: number, unitQuantity: number, targetCount: number): RefillQuoteLine | null {
    let curveQuantity: number;
    let multiplier: number;
    switch (kind) {
        case "custom_quantity":
        case "split_total":
            curveQuantity = totalQuantity;
            multiplier = 1;
            break;
        case "per_post_each":
        case "per_video_each":
        case "future_posts_plan":
            curveQuantity = unitQuantity;
            multiplier = targetCount;
            break;
        default:
            return null;
    }
    if (!isPositiveInt(curveQuantity) || !isPositiveInt(multiplier) || curveQuantity * multiplier !== totalQuantity) return null;
    return { curveQuantity, multiplier };
}

/** service.NewPriceCurve over the sellable rows: sorted knots, or null when they form no curve. */
function sellableCurve(ladder: readonly RefillLadderRow[]): RefillLadderRow[] | null {
    const knots = ladder
        .filter((row) => row && row.sellable === true)
        .map((row, index) => ({ row, index }))
        .sort((a, b) => a.row.quantity - b.row.quantity || a.index - b.index)
        .map(({ row }) => row);
    if (knots.length === 0) return null;
    for (let i = 0; i < knots.length; i++) {
        if (!isPositiveInt(knots[i].quantity)) return null;
        if (i > 0 && knots[i - 1].quantity === knots[i].quantity) return null;
    }
    return knots;
}

function usdOf(row: RefillLadderRow): bigint | null {
    const usd = row.usdMinor;
    return typeof usd === "number" && Number.isInteger(usd) && usd >= 0 ? BigInt(usd) : null;
}

/** Floor division for a positive divisor (big.Int.Div is Euclidean: floor, also for a negative numerator). */
function floorDiv(num: bigint, den: bigint): bigint {
    const q = num / den; // truncates toward zero
    return num % den !== BigInt(0) && (num < BigInt(0)) !== (den < BigInt(0)) ? q - BigInt(1) : q;
}

/**
 * The refill basis of a quote over the quote locale's ladder — QuoteService.quoteRefillListBasis +
 * models.RefillListBasis.ListAmountUSDMinor, step for step. `null` exactly where the API states no
 * basis (and the order would be refused): no line, no curve, a point outside the ladder, a knot
 * without a USD list price, two knots of one package, a line over 2^31−1 units, or an amount the
 * browser cannot hold exactly (above 2^53 cents; the API's own bound is 2^63).
 */
export function refillListBasisForQuote(ladder: readonly RefillLadderRow[], lines: readonly RefillQuoteLine[]): RefillListBasis | null {
    if (!Array.isArray(lines) || lines.length === 0) return null;
    const curve = sellableCurve(ladder ?? []);
    if (!curve) return null;
    const minQ = curve[0].quantity;
    const maxQ = curve[curve.length - 1].quantity;
    let sum = BigInt(0);
    const out: RefillListBasis["lines"] = [];
    for (const line of lines) {
        const { curveQuantity: q, multiplier: m } = line ?? ({} as RefillQuoteLine);
        if (!isPositiveInt(q) || !isPositiveInt(m) || q * m > MAX_INT32) return null;
        if (q < minQ || q > maxQ) return null; // never extrapolated
        let i = 0;
        while (i + 1 < curve.length && curve[i + 1].quantity <= q) i++;
        const knots = curve[i].quantity === q ? [curve[i]] : [curve[i], curve[i + 1]];
        if (knots.length === 2 && knots[0].packageId && knots[0].packageId === knots[1].packageId) return null;
        const prices = knots.map(usdOf);
        if (prices.some((p) => p === null)) return null;
        let point = prices[0] as bigint;
        if (knots.length === 2) {
            const [lo, hi] = knots;
            const step = floorDiv(((prices[1] as bigint) - point) * BigInt(q - lo.quantity), BigInt(hi.quantity - lo.quantity));
            point += step;
        }
        const amount = point * BigInt(m);
        if (amount < BigInt(0)) return null;
        sum += amount;
        out.push({ knots, curveQuantity: q, multiplier: m });
    }
    if (sum > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    const amountUsdMinor = Number(sum);
    const days = refillDaysForUsdMinor(amountUsdMinor);
    return days === null ? null : { amountUsdMinor, lines: out, days };
}

/** The refill band of a quote (the band the API stamps on its order), or null where it states none. */
export function refillDaysForQuote(ladder: readonly RefillLadderRow[], lines: readonly RefillQuoteLine[]): RefillDays | null {
    return refillListBasisForQuote(ladder, lines)?.days ?? null;
}

/**
 * A package card row as the selector holds it → a ladder row. `price` is the list price in the
 * locale's pricing currency (price_tr on the Turkish site, price_en elsewhere) and is read ONLY for
 * sellability (> 0), never as an amount; the amount comes from `priceEn` alone.
 */
export function refillLadderFromPackages(
    packages: readonly { id?: string; qty: number; price: number; priceEn: number | null | undefined }[],
): RefillLadderRow[] {
    return packages.map((p) => ({
        packageId: p.id,
        quantity: p.qty,
        sellable: typeof p.price === "number" && Number.isFinite(p.price) && p.price > 0,
        usdMinor: usdToMinor(p.priceEn),
    }));
}

// ── The term an ORDER carries ────────────────────────────────────────────────────────────────

/** The refill fields the API sends on an order (admin and customer payloads). */
export interface StampedRefillFields {
    /** 30, 90 or 365 when stamped; null on an order written before the policy; absent on an older API. */
    refill_days?: number | null;
    /** "refill-2026-10-01" when stamped; null on a legacy order. */
    refill_policy_version?: string | null;
    /** When delivery finished: the instant the refill window starts counting from. */
    completed_at?: string | null;
}

export type OrderRefillTerm =
    /** The term stamped on the order when it was written. */
    | { kind: "stamped"; days: RefillDays; version: string }
    /**
     * No stamp: the order was written before the policy (or by an API that does not stamp yet).
     * It keeps the terms it was sold under; no band computed today may be shown for it.
     */
    | { kind: "legacy" };

/**
 * The refill term of an order — the STAMP, never a band recomputed from today's catalogue.
 * A stamp is accepted only when both fields are present and the days are a policy length.
 */
export function orderRefillTerm(order: StampedRefillFields | null | undefined): OrderRefillTerm {
    const days = order?.refill_days;
    const version = typeof order?.refill_policy_version === "string" ? order.refill_policy_version.trim() : "";
    if (isRefillDays(days) && version) return { kind: "stamped", days, version };
    return { kind: "legacy" };
}

/**
 * Epoch ms the refill window of a stamped order closes: `completed_at` + days × 24 h, in UTC
 * (backend `models.RefillWindowEndsAt`). `null` until delivery has finished, for an unparseable
 * or zero-value `completed_at`, and for a days value that is not a policy length.
 */
export function refillWindowEndMs(completedAt: string | null | undefined, days: number | null | undefined): number | null {
    if (!isRefillDays(days)) return null;
    if (typeof completedAt !== "string" || !completedAt.trim()) return null;
    const start = Date.parse(completedAt.trim());
    // Go zero-value timestamps ("0001-01-01T00:00:00Z") parse and mean "no value".
    if (!Number.isFinite(start) || start <= 0) return null;
    return start + days * DAY_MS;
}

// ── How copy states the bands ────────────────────────────────────────────────────────────────

export type RefillCopyLocale = "tr" | "en" | "de" | "es" | "fr" | "pt";

/**
 * The last cent of a band whose next band starts at `edgeUsd` ("299.99" / "299,99"): the middle band is written with
 * both of its ends, so no reader can take "$100 to $300" as including $300 (G03 N5, 2026-10-02).
 */
function lastCentBelow(edgeUsd: number, decimalComma: boolean): string {
    const text = ((Math.round(edgeUsd * 100) - 1) / 100).toFixed(2);
    return decimalComma ? text.replace(".", ",") : text;
}

/**
 * The band table in words, per locale, built from REFILL_BANDS. Copy that states the bands WITH
 * the price edges (legal, FAQ, checkout, about) quotes exactly this phrase; the claims gate
 * (lib/__tests__/claim-gate.test.ts) fails a refill sentence that names a USD amount without it.
 * Copy that may not print prices (route intent copy) says "30, 90 or 365 days, by the package's
 * USD list price" instead.
 *
 * Every band names both of its ends and the top band says "and above" in the locale's words
 * (under $100 / $100 to $299.99 / $300 and above), so neither edge can be read two ways (G03 N5:
 * "90 days from $100 up to $300" could be read as including $300). Mirrored by REFILL_BAND_PHRASE in
 * scripts/claim-matrix.py (lib/__tests__/refill-policy.test.ts keeps the two equal).
 */
export function refillBandPhrase(locale: RefillCopyLocale): string {
    const [short, mid, long] = REFILL_POLICY_DAYS;
    const [low, high] = REFILL_EDGES_USD;
    const top = lastCentBelow(high, locale !== "en");
    switch (locale) {
        case "tr":
            return `liste fiyatı ${low} USD'nin altındaysa ${short} gün, ${low} USD ile ${top} USD arasındaysa ${mid} gün, ${high} USD veya üzerindeyse ${long} gün`;
        case "en":
            return `${short} days if the list price is under $${low}, ${mid} days from $${low} to $${top}, and ${long} days at $${high} and above`;
        case "de":
            return `${short} Tage bei einem Listenpreis unter ${low} USD, ${mid} Tage von ${low} USD bis ${top} USD und ${long} Tage ab ${high} USD`;
        case "es":
            return `${short} días si el precio de lista es inferior a ${low} USD, ${mid} días de ${low} USD a ${top} USD y ${long} días a partir de ${high} USD`;
        case "fr":
            return `${short} jours si le prix catalogue est inférieur à ${low} USD, ${mid} jours de ${low} USD à ${top} USD et ${long} jours à partir de ${high} USD`;
        case "pt":
            return `${short} dias se o preço de tabela for inferior a ${low} USD, ${mid} dias de ${low} USD a ${top} USD e ${long} dias a partir de ${high} USD`;
    }
}
